import { useModal } from "@/Contexts";
import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { PinIcon } from "@/Icons/v2/Pin";
import { useTerminalsStateRE } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { CLIPPED_BUTTON_STYLE } from "@/constants/general";
import { useTrans } from "@/translations";

import { useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import { MessageForm } from "./components/messageForm";
import { MessageView } from "./components/messageView";

export const PinnedMessageView = () => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsStateRE();
  const { hideModal } = useModal();
  const { setGlobalAlert } = useGlobalAlert();

  const [editMode, setEditMode] = useState(false);

  const {
    data: newsfeedPost,
    refetch: refetchNewsfeedPost,
    isLoading: isLoadingNewsfeedPost,
  } = trpc.newsfeed.getMainNewsfeedPostWithImageUrl.useQuery({
    terminalId: currentTerminal.id,
  });

  const { mutate: deleteNewsfeedImageAndOrPost } =
    trpc.newsfeed.deleteNewsfeedImageAndOrPost.useMutation({
      onSuccess: async () => {
        refetchNewsfeedPost().then(() => hideModal());
      },
      onError: (error) => {
        setGlobalAlert({
          type: "error",
          message: "errors:newsfeedPostDeleteFailed",
        });
        console.error("Error deleting newsfeed post:", error);
      },
    });

  const view = useMemo(() => {
    if (isLoadingNewsfeedPost) {
      return (
        <div className="flex h-full min-h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    if (!newsfeedPost && !editMode) {
      return (
        <div className="flex flex-col gap-[12px] p-[22px]">
          <div className="text-[14px] text-[#9a958f]">
            {t("libraryPage:messageHint")}
          </div>
          <button
            type="button"
            className={twMerge(
              "inline-flex w-fit cursor-pointer items-center gap-[9px] bg-black px-[22px] py-[9px] text-[13px] font-semibold text-white hover:bg-[#3a3838]",
              CLIPPED_BUTTON_STYLE,
            )}
            onClick={() => setEditMode(true)}
          >
            {t("libraryPage:addMessageButton")}
          </button>
        </div>
      );
    }

    if (editMode) {
      return (
        <MessageForm
          newsfeedPost={newsfeedPost}
          setEditMode={setEditMode}
          deleteImage={deleteNewsfeedImageAndOrPost}
          refetchNewsfeedPost={refetchNewsfeedPost}
        />
      );
    }

    if (newsfeedPost) {
      return (
        <MessageView
          newsfeedPost={newsfeedPost}
          setEditMode={setEditMode}
          deleteMessage={deleteNewsfeedImageAndOrPost}
        />
      );
    }

    return <></>;
  }, [
    isLoadingNewsfeedPost,
    newsfeedPost,
    editMode,
    t,
    deleteNewsfeedImageAndOrPost,
    refetchNewsfeedPost,
  ]);

  return (
    <div className="border-grey2 mt-[22px] w-full rounded-[10px] border border-1 bg-white">
      <div className="border-b-grey2 flex w-full flex-row items-center gap-[10px] border-b border-b-1 px-[20px] py-[13px]">
        <PinIcon />
        <div className="text-[11px] font-bold tracking-[0.08em] text-[#1d1c1c] uppercase">
          {t("libraryPage:pinnedMessageTitle")}
        </div>
        <div className="text-[11.5px] text-[#9a958f]">
          {t("libraryPage:pinnedMessageDescription")}
        </div>
      </div>
      <div className="h-full flex-1 p-[22px]">{view}</div>
    </div>
  );
};
